import React from 'react'
import classNames from 'classnames'
import { type CustomIntegration } from '../../../models/CustomIntegration'
import { Button } from '../../../components/Button/Button'
import '../../../components/AppCard/AppCard.scss'
import styles from './CustomIntegrationHeader.module.scss'

export type CustomIntegrationConnectionState = 'connected' | 'reconnectRequired' | null

interface CustomIntegrationHeaderProps {
  customIntegration: CustomIntegration
  canManage: boolean
  isToggling: boolean
  onToggleActive: () => void
  connectionState: CustomIntegrationConnectionState
  onConnect: () => void
}

export const CustomIntegrationHeader: React.FC<CustomIntegrationHeaderProps> = ({
  customIntegration,
  canManage,
  isToggling,
  onToggleActive,
  connectionState,
  onConnect,
}) => {
  const { name, imageUrl, active, auth } = customIntegration
  const canConnect = active && auth.type !== 'none'
  const isConnected = connectionState === 'connected'
  const needsReconnect = active && connectionState === 'reconnectRequired'
  const initials = name.slice(0, 2).toUpperCase() || '?'

  return (
    <div className='d-flex align-items-center justify-content-between gap-4 py-4'>
      <div className='d-flex align-items-center zocks-gap-1.25'>
        {imageUrl ? (
          <img src={imageUrl} alt={`${name} logo`} className={classNames('flex-shrink-0', styles.icon)} />
        ) : (
          <div className={classNames('d-flex align-items-center justify-content-center rounded zocks-bg-color-gray-100 flex-shrink-0', styles.icon)}>
            <span className='app-icon-initials'>{initials}</span>
          </div>
        )}
        <h3 className='mb-0'>{name}</h3>
      </div>
      <div className='d-flex align-items-center gap-3 flex-shrink-0'>
        <span
          className={classNames('d-flex align-items-center zocks-gap-0.25 paragraph-medium', { 'zocks-text-success': active, 'zocks-text-gray-600': !active })}
        >
          <i className={classNames('bi fs-6', { 'bi-check-lg': active, 'bi-dash-circle': !active })} aria-hidden='true' />
          {active ? 'Enabled' : 'Disabled'}
        </span>
        {isConnected ? (
          <span className='d-flex align-items-center zocks-gap-0.25 paragraph-medium zocks-text-success'>
            <i className='bi bi-link-45deg fs-6' aria-hidden='true' />
            Connected
          </span>
        ) : null}
        {needsReconnect ? (
          <span className='d-flex align-items-center zocks-gap-0.25 paragraph-medium zocks-text-warning-text'>
            <i className='bi bi-exclamation-triangle fs-6' aria-hidden='true' />
            Reconnect required
          </span>
        ) : null}
        {canConnect ? (
          <Button variant='primary' onClick={onConnect}>
            {connectionState === null ? 'Connect' : 'Reconnect'}
          </Button>
        ) : null}
        {canManage ? (
          <Button variant={active ? 'primaryGhost' : 'primary'} onClick={onToggleActive} loading={isToggling}>
            {active ? 'Disable' : 'Enable'}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
